import { DateTime } from 'luxon';
import PropTypes from 'prop-types';
import React, { useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { categoriesSelectors } from 'app-components/flux/categories';
import { transactionsActions, transactionsSelectors } from 'app-components/flux/transactions';

import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import DialogContent from '@mui/material/DialogContent';
import Slide from '@mui/material/Slide';
import Typography from '@mui/material/Typography';
import ZeroStateView from 'components/ZeroStateView';
import { makeStyles } from 'tss-react/mui';

import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { boxShadowsToDropShadows } from 'themes/createTheme';

import RefundImage from 'assets/illustrations/Refund.png';
import StdDialog from 'components/Dialogs/StdDialog';
import QPanelButton from 'components/QPanelButton';
import AmountField from 'components/QuickenControls/AmountField';

export const DIALOG_TYPE_REFUND_MATCH = 'DIALOG_TYPE_REFUND_MATCH';

const useStyles = makeStyles()((theme) => ({
  receiptShadow: {
    filter: boxShadowsToDropShadows(theme.shadows[3]),
    transition: '300ms',
    '&:hover': {
      filter: boxShadowsToDropShadows(theme.shadows[5]),
    },
  },
  panelButton: {
    clipPath: theme.shape.bottomCutOffClipPath,
    boxShadow: 'none',
  },
  payee: {
    width: 160,
    margin: 8,
    textAlign: 'left',
  },
  date: {
    width: 100,
    margin: 8,
  },
  amount: {
    width: 60,
    margin: 8,
  },
}));

const RefundMatchDialog = (props) => {
  const { transaction } = props;

  const dispatch = useDispatch();
  const { classes } = useStyles();

  const refundActiveTransactions = useSelector((state) =>
    transactionsSelectors.getRefundActiveTransactionsByAccountId(state, transaction?.accountId),
  );
  const categoryIds = useMemo(
    () =>
      refundActiveTransactions
        .filter((txn) => txn.coa?.type === 'CATEGORY')
        .map((txn) => txn.coa.id)
        .toSet(),
    [refundActiveTransactions],
  );
  const categories = useSelector((state) => categoriesSelectors.getCategoriesForIds(state, categoryIds));
  const showCategoryIcons = useSelector((state) => featureFlagsSelectors.getFeatureFlag(state, 'categoryIcons'));

  const stdDialogRef = useRef();

  return (
    <StdDialog
      stdDialogRef={stdDialogRef}
      sharedcomponentid='RefundMatchDialog'
      size='md'
      title='Match refund'
      {...props}
    >
      <DialogContent>
        {Boolean(!refundActiveTransactions.size) && (
          <ZeroStateView
            size='small'
            description="We couldn't find any refund tracking records"
            qIllustration='boxEmpty'
          />
        )}

        {Boolean(refundActiveTransactions.size) && (
          <Box display='flex' paddingRight={12}>
            <Box m={3}>
              <img alt='refund tracker' src={RefundImage} width={72} height={72} />
            </Box>
            <Box display='flex' flexDirection='column'>
              <Typography>Select the corresponding refund for this transaction.</Typography>

              {refundActiveTransactions.toIndexedSeq().map((refundTransaction, index) => (
                <Slide key={refundTransaction?.id} in appear direction='left' style={{ transitionDelay: index * 30 }}>
                  <div className={classes.receiptShadow}>
                    <QPanelButton
                      id={`refund-id-${index}`}
                      key={refundTransaction.id}
                      onClick={() => {
                        dispatch(
                          transactionsActions.performTransactionAction(
                            {
                              action: 'match',
                              id: refundTransaction.id,
                              otherId: transaction.id,
                            },
                            { undo: { userMessage: 'Refund matched.' } },
                          ),
                        );
                        stdDialogRef.current?.closeDialog('refundMatched');
                      }}
                      className={classes.panelButton}
                      startIcon={
                        <Avatar
                          aria-label='category logo'
                          variant='rounded'
                          src={showCategoryIcons && categories.get(refundTransaction.coa?.id)?.logoUrl}
                        >
                          {refundTransaction?.payee?.[0]?.toUpperCase?.()}
                        </Avatar>
                      }
                      title={
                        <Box display='flex'>
                          <Typography className={classes.payee}>{refundTransaction.payee}</Typography>

                          <Typography className={classes.date}>
                            {DateTime.fromISO(refundTransaction.postedOn).toLocaleString(DateTime.DATE_MED)}
                          </Typography>

                          <Typography align='right' className={classes.amount}>
                            <AmountField value={refundTransaction.amount} />
                          </Typography>
                        </Box>
                      }
                    />
                  </div>
                </Slide>
              ))}
            </Box>
          </Box>
        )}
      </DialogContent>
    </StdDialog>
  );
};

RefundMatchDialog.propTypes = {
  transaction: PropTypes.object,
  dialogId: PropTypes.string, // set automatically for uncontrolled dialog
};

export default RefundMatchDialog;
